import { database } from "..";
import Product from "../model/product";
import Proposal from "../model/proposal";
import { Q } from "@nozbe/watermelondb";
import ProposalLineItem from "../model/proposalLineItem";
import { formatDateString } from "../utils";
import Customer from "../model/customer";

export type QuickbooksProduct = {
  productServiceName: string;
  description: string;
  type: string;
  price: number;
  taxable: string;
};

export type QuickbooksInvoice = {
  invoiceNumber: string;
  customer: string;
  invoiceDate: string;
  dueDate: string;
  item: string;
  itemDescription: string;
  itemQuantity: number;
  itemRate: number;
  itemAmount: number;
  taxable: string;
  taxRate: string;
  email: string;
  phone: string;
  customerAddressLine1: string;
  customerAddressLine2: string;
  customerCity: string;
  customerState:string;
  customerZip: string;
  serviceAddressLine1: string;
  serviceAddressLine2: string;
  serviceCity: string;
  serviceState: string;
  serviceZip: string;
  taxName: string;
  status: string;
};

export type QuickbooksCustomer = {
  name: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export async function getAllProducts(): Promise<QuickbooksProduct[]> {
  const productCollection = database.get<Product>("products");
  let query = productCollection.query();
  const dbProducts: Product[] = await query.fetch();
  const products = dbProducts.map((v: Product) => ({
    productServiceName: v.title || "",
    description: v.description || "",
    type: "Service",
    price: v.price || 0.0,
    taxable: v.taxable ? "Yes" : "No",
  }));
  return products;
}

export async function getInvoiceLines(
  startDate?: Date,
  endDate?: Date
): Promise<QuickbooksInvoice[]> {
  const proposalCollection = database.get<Proposal>("proposals");
  let query = proposalCollection.query();
  if (startDate) {
    query = query.extend(Q.where("invoice_date", Q.gte(startDate.getTime())));
  }
  if (endDate) {
    query = query.extend(
      Q.where("invoice_date", Q.lte(endDate.getTime() + 86400 * 1000))
    );
  }
  const proposals: Proposal[] = await query.fetch();
  const proposalLines = await Promise.all(
    proposals.map(async (p: Proposal) => {
      const proposalLineCollection = database.get<ProposalLineItem>(
        "proposal_line_items"
      );
      const lines = await proposalLineCollection
        .query(Q.where("proposal_id", p.id))
        .fetch();
      return Promise.all(
        lines.map(async (l: ProposalLineItem) => {
          const productInfo = await database
            .get<Product>("products")
            .find(l.product?.id || "");
          return {
            invoiceNumber: p.id,
            customer: `${p.firstName} ${p.lastName}`,
            phone: p.phone || "",
            customerAddressLine1: p.addressLine1 || "",
            customerAddressLine2: p.addressLine2 || "",
            customerCity: p.city || "",
            customerState: p.state || "",
            customerZip: p.zip || "",
            serviceAddressLine1: p.serviceAddressLine1 || "",
            serviceAddressLine2: p.serviceAddressLine2 || "",
            serviceCity: p.serviceCity || "",
            serviceState: p.serviceState || "",
            serviceZip: p.serviceZip || "",
            invoiceDate: p.invoiceDate
              ? formatDateString(new Date(p.invoiceDate))
              : "",
            dueDate: p.invoiceDueDate
              ? formatDateString(new Date(p.invoiceDueDate))
              : "",
            item: l.productName || "",
            itemDescription: l.productNotes || "",
            itemQuantity: l.quantity || 0,
            itemRate: l.unitPrice || 0,
            itemAmount: (l.quantity || 0) * (l.unitPrice || 0),
            taxName: p.taxName || "",
            taxable: productInfo.taxable ? "Y" : "N",
            taxRate: `${p.taxRate}%` || "",
            email: p.email || "",
            status: p.status || ""
          };
        })
      );
    })
  );
  return proposalLines.flat();
}

export async function getQuickbooksCustomers(): Promise<QuickbooksCustomer[]> {
  const customerCollection = database.get<Customer>("customers");
  let query = customerCollection.query();
  const dbCustomer: Customer[] = await query.fetch();
  const customers = dbCustomer.map((v: Customer) => ({
    name: `${v.firstName} ${v.lastName}`,
    email: v.email || "",
    phone: v.phone || "",
    street: v.addressLine1 || "",
    city: v.city || "",
    state: v.state || "",
    zip: v.zip || "",
    country: "United States",
  }));
  return customers;
}